import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { forwardRef } from 'react';

type ButtonVariant = 'primary' | 'secondary' | 'link' | 'danger';

export function Button({
  variant = 'primary',
  size = 'md',
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; size?: 'sm' | 'md' }) {
  const styles = {
    primary: 'bg-espresso text-white hover:bg-espresso/90 disabled:bg-sand disabled:text-muted',
    secondary: 'border border-espresso bg-white text-espresso hover:bg-cream disabled:opacity-50',
    danger: 'border border-danger bg-white text-danger hover:bg-danger/5 disabled:opacity-50',
    link: 'text-espresso underline underline-offset-4 hover:text-clay disabled:opacity-50',
  }[variant];
  const sizing = variant === 'link' ? '' : size === 'sm' ? 'h-9 rounded-dar px-3 text-sm font-bold' : 'h-11 rounded-dar px-5 font-bold';
  return <button type="button" className={`${sizing} ${styles} transition-colors ${className}`} {...props} />;
}

const inputBase = 'w-full rounded-dar border bg-white px-3 outline-none focus:border-espresso disabled:bg-cream';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }>(
  function Input({ invalid, className = '', ...props }, ref) {
    return (
      <input ref={ref} className={`h-11 ${inputBase} ${invalid ? 'border-danger' : 'border-sand'} ${className}`} {...props} />
    );
  },
);

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }
>(function Textarea({ invalid, className = '', rows = 4, ...props }, ref) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      className={`py-2 ${inputBase} ${invalid ? 'border-danger' : 'border-sand'} ${className}`}
      {...props}
    />
  );
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement> & { invalid?: boolean }>(
  function Select({ invalid, className = '', ...props }, ref) {
    return (
      <select ref={ref} className={`h-11 ${inputBase} ${invalid ? 'border-danger' : 'border-sand'} ${className}`} {...props} />
    );
  },
);

export const Checkbox = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { label: ReactNode }>(
  function Checkbox({ label, className = '', ...props }, ref) {
    return (
      <label className={`inline-flex cursor-pointer items-center gap-2 ${className}`}>
        <input ref={ref} type="checkbox" className="size-4 accent-espresso" {...props} />
        <span>{label}</span>
      </label>
    );
  },
);

export function Field({
  label,
  htmlFor,
  error,
  hint,
  children,
}: {
  label: ReactNode;
  htmlFor?: string;
  error?: string | undefined;
  hint?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={htmlFor} className="block text-sm font-bold">
        {label}
      </label>
      {children}
      {hint ? <p className="text-xs text-muted">{hint}</p> : null}
      {error ? <p className="text-sm text-danger">{error}</p> : null}
    </div>
  );
}

export function Section({ title, actions, children }: { title: ReactNode; actions?: ReactNode; children: ReactNode }) {
  return (
    <section className="rounded-dar border border-sand bg-white">
      <header className="flex items-center justify-between gap-3 border-b border-sand px-5 py-3">
        <h2 className="font-bold">{title}</h2>
        {actions}
      </header>
      <div className="space-y-4 p-5">{children}</div>
    </section>
  );
}

export function PageHeader({ title, actions }: { title: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
      <h1 className="font-heading text-2xl">{title}</h1>
      <div className="flex flex-wrap items-center gap-2">{actions}</div>
    </div>
  );
}

export function Badge({ tone = 'neutral', children }: { tone?: 'neutral' | 'dark' | 'clay' | 'muted'; children: ReactNode }) {
  const styles = {
    neutral: 'bg-sand text-espresso',
    dark: 'bg-espresso text-white',
    clay: 'bg-clay text-white',
    muted: 'border border-dashed border-muted text-muted',
  }[tone];
  return <span className={`inline-block rounded px-2 py-0.5 text-xs font-bold ${styles}`}>{children}</span>;
}

export function Notice({ tone = 'info', children }: { tone?: 'info' | 'error' | 'success'; children: ReactNode }) {
  const styles = {
    info: 'bg-cream text-espresso',
    error: 'bg-danger/10 text-danger',
    success: 'bg-sand text-espresso',
  }[tone];
  return (
    <p role={tone === 'error' ? 'alert' : 'status'} className={`rounded-dar p-3 text-sm ${styles}`}>
      {children}
    </p>
  );
}

export function Loading() {
  return <p className="py-10 text-center text-muted">لحظة…</p>;
}
